import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';

const PartnerLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, authenticating } = usePartnerAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError(new Error('Please enter your email and password.'));
      return;
    }
    try {
      await login({ email: email.trim(), password });
      const redirectTo = location.state?.from?.pathname || '/partner/dashboard';
      navigate(redirectTo, { replace: true });
    } catch (authError) {
      const message =
        authError?.response?.data?.error ||
        authError?.message ||
        'Authentication failed. Please check your credentials.';
      setError(new Error(message));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%', boxShadow: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Partner Portal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in with your Studio Sivad partner account to manage production work, review
              orders, and maintain your organization profile.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error.message}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              fullWidth
              placeholder="you@factory.com"
              autoFocus
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              fullWidth
              placeholder="Enter your password"
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={authenticating}
              startIcon={authenticating ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {authenticating ? 'Signing in...' : 'Sign in'}
            </Button>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Need access? Contact your Studio Sivad account manager to receive an invitation or
              reset your credentials.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PartnerLoginPage;
